import { useEffect, useState } from 'react';

interface Transaction {
  id: number;
  type: 'income' | 'expense';
  amount: number;
  category: string;
  description: string;
  date: string;
}

export default function FinancialSummary() {
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netBalance, setNetBalance] = useState(0);

  useEffect(() => {
    const calculateFinancials = () => {
      const transactions: Transaction[] = JSON.parse(localStorage.getItem('transactions') || '[]');
      
      const income = transactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const expenses = transactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + t.amount, 0);
      
      setTotalIncome(income);
      setTotalExpenses(expenses);
      setNetBalance(income - expenses);
    };

    calculateFinancials();

    const handleStorageChange = () => {
      calculateFinancials();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('transactionsUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('transactionsUpdated', handleStorageChange);
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const summaryCards = [
    {
      title: 'Total Income',
      amount: formatCurrency(totalIncome),
      change: '+12.5%',
      isPositive: true,
      icon: 'ri-arrow-up-line',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: 'Total Expenses',
      amount: formatCurrency(totalExpenses),
      change: '+8.2%',
      isPositive: false,
      icon: 'ri-arrow-down-line',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
    },
    {
      title: 'Net Balance',
      amount: formatCurrency(netBalance),
      change: '+18.3%',
      isPositive: true,
      icon: 'ri-wallet-line',
      bgColor: 'bg-teal-50',
      iconColor: 'text-teal-600',
    },
  ];

  return (
    <>
      {summaryCards.map((card, index) => (
        <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 ${card.bgColor} rounded-lg flex items-center justify-center`}>
              <i className={`${card.icon} text-2xl ${card.iconColor} w-6 h-6 flex items-center justify-center`}></i>
            </div>
            <span className={`text-sm font-medium ${card.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {card.change}
            </span>
          </div>
          <h3 className="text-sm text-gray-600 mb-1">{card.title}</h3>
          <p className="text-2xl font-bold text-gray-900">{card.amount}</p>
        </div>
      ))}
    </>
  );
}
